import React, { useCallback, useEffect, useRef, useState } from 'react';
import Loader from '../../Components/Loader/Loader';
import SidebarLoadError from '../../Components/RightSidebar/SidebarLoadError';
import sidebarStyles from './SideBarStyle.module.css';
import { postpaymentData, addSaleNote, postProductPayment, checkOutletAvailability, getProductsList, getSalesDetails, postMembershipTransactionPayment, getPaymentDetails, getCustomerDetails } from '../../APICalling/APICalling';
import SaleBookingInfo from '../../Components/RightSidebar/SaleBookingInfo';
import SaleInfoPage from '../../Components/RightSidebar/SaleInfoPage';
import CheckoutPage from '../../Components/RightSidebar/CheckoutPage';
import PaymentPage from '../../Components/RightSidebar/PaymentPage';
import ConfirmationPage from '../../Components/RightSidebar/ConfirmationPage';
import DiscardPaymentPopup from '../../Components/Popups/DiscardPaymentPopup/DiscardPaymentPopup';
import SaleNotePopup from '../../Components/Popups/SaleNotePopup/SaleNotePopup';
import { SaleNote } from '../../Components/SaleNote/SaleNote';
import { useGlobalContext } from '../../Context/GlobalContext';
import { getOutletExtendedFlagForDate, isBookingAlreadyCompletedResponse, roundCashAmount } from '../../Utils/utils';
import { buildMembershipIdPaymentFields } from '../../Utils/customerSearchUtils';
import {
  refreshCustomerCreditDetailsFromUser,
  computeRemainingCheckoutAmount,
  membershipClientToTopupCustomer,
  fetchMembershipClientCreditPatch,
} from '../../Utils/customerCreditRefresh';
import {
  fetchAndStoreCheckoutCustomerPasswordFlag,
  enrichCustomerWithCheckoutPasswordFlag,
} from '../../Utils/customerPasswordSession';
import { showAppToast } from '../../Utils/appToast';
import MembershipSaleSidebarInfo from './MembershipSaleSidebarInfo';
import CustomerDetailsModal from '../../Components/CustomerDetailsModal/CustomerDetailsModal';
import { MembershipTxnPaymentDetailSheet } from '../../Components/MembershipTransactionSidePanel/MembershipTransactionSidePanel';
import logger from '../../Utils/logger';
import { buildSeededPricingOverrides, mergePricingOverrideMap } from '../../Components/RightSidebar/checkoutPricingModifiers';

// This component is dedicated for Sales page status-click details view.
// Home calendar/sidebar share GlobalContext — snapshot & restore booking sidebar
// state, and never wipe bookingsList on mere open/load.
const HOME_BOOKINGS_REFRESH_FLAG = 'forceHomeCalendarRefresh';

const markHomeCalendarNeedsRefresh = () => {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(HOME_BOOKINGS_REFRESH_FLAG, '1');
};

/** After a real mutation, mark Home to refresh; clear list only if Home is mounted. */
const triggerHomeCalendarRefreshIfMounted = (setBookingsList) => {
  markHomeCalendarNeedsRefresh();
  if (typeof setBookingsList === 'function') {
    setBookingsList([]);
  }
};

const DEFAULT_PAYMENT_SUMMARY = {
  subtotal: 0,
  midtotal: 0,
  gst: 0,
  tip: '0.00',
  total: 0,
  payment_method: '',
};

const MEMBERSHIP_SALE_DETAILS_QUERY = {
  panel: 'outlet',
  type: 'membership',
};

const SALE_DETAILS_ERROR_FALLBACK = "Could not load sale details. Please try again.";

const getSalesDetailsFailureMessage = (response) => {
  const nested = response?.response?.data;
  const body = nested && typeof nested === "object" && !Array.isArray(nested) ? nested : response;
  const failed = body?.success === false || body?.status === false;
  if (!failed) return "";
  const apiMessage = typeof body?.message === "string" ? body.message.trim() : "";
  return apiMessage || SALE_DETAILS_ERROR_FALLBACK;
};

const extractSaleIdFromPaymentResponse = (response, fallbackId = null) => {
  const data = response?.data?.data || response?.data || {};
  const candidates = [data?.booking_id,data?.sale_id,data?.id,response?.data?.booking_id,response?.data?.sale_id,fallbackId,];

  for (const candidate of candidates) {
    if (candidate != null && String(candidate).trim() !== '') {
      return String(candidate).replace('#', '');
    }
  }

  return null;
};

const buildProductPaymentItemsFromBookingData = (bookingData) => {
  const customerName = bookingData?.user_name || '';
  const voucherItems = Array.isArray(bookingData?.voucher_items) ? bookingData.voucher_items : [];

  if (voucherItems.length > 0) {
    return voucherItems.reduce((items, voucher) => {
      const serial = String(voucher?.serial_number || '').trim();
      if (!serial) return items;
      items.push({
        item_type: 'voucher',
        serial_number: serial,
      });
      return items;
    }, []);
  }

  const productItems = Array.isArray(bookingData?.product_items) ? bookingData.product_items : [];
  return productItems.map((item, index) => {
    const baseItem = {
      item_type: 'product',
      product: (item.product_id || item.id || item.product || 1).toString(),
      price: (item.original_price || item.price || 0).toString(),
      quantity: (item.quantity || 1).toString(),
      customer_name: customerName,
      item_number: index + 1,
      reason: item.reason || '',
      membership: Number(item.membership ?? item.isMembershipApplied ?? 0) === 1 ? 1 : 0,
    };

    if (item.changed_price && item.changed_price !== item.original_price) {
      baseItem.change_price = item.changed_price.toString();
    }

    return baseItem;
  });
};

const resolveCheckoutSaleType = (saleType, bookingData) => {
  const normalizedSaleType = String(saleType || '').trim().toLowerCase();
  if (normalizedSaleType === 'voucher') return 'voucher';
  if (normalizedSaleType === 'product') return 'product';

  const apiType = String(bookingData?.type || bookingData?.booking_type || '').trim().toLowerCase();
  if (apiType === 'voucher') return 'voucher';

  if (Array.isArray(bookingData?.voucher_items) && bookingData.voucher_items.length > 0) {
    return 'voucher';
  }

  return 'product';
};

const BookingDetailsFromSalesSidebar = ({ bookingId, bookingType, initialStep = null, onStepChange, onSaleNoteChange, onBookingIdResolved, active, onShowVoidSalePopup, onCloseSidebar, onSalesSidebarMeta, onSaleStatusResolved, onOpenRelatedSale }) => {
  const [loading, setLoading] = useState(true);
  const [saleDetailsError, setSaleDetailsError] = useState("");
  const [saleDetailsAttempt, setSaleDetailsAttempt] = useState(0);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [bookingDataFromAPI, setBookingDataFromAPI] = useState(null);
  const [customerDataFromAPI, setCustomerDataFromAPI] = useState(null);
  const [currentStep, setCurrentStep] = useState('info');
  const isProductSale = bookingType === 'product' || bookingType === 'voucher';
  const isMembershipSale = bookingType === 'membership';
  const isServiceBooking = !isMembershipSale && (bookingType === 'booking' || bookingType === 'service');
  const [membershipDetail, setMembershipDetail] = useState(null);
  const [membershipPaymentDetails, setMembershipPaymentDetails] = useState([]);
  const [isMembershipPaymentDetailOpen, setIsMembershipPaymentDetailOpen] = useState(false);
  const [selectedMembershipPayment, setSelectedMembershipPayment] = useState(null);
  const saleDetailsRequestRef = useRef(0);

  const normalizeStatusKey = (value) =>
    String(value || '').trim().toLowerCase().replace(/\s+/g, '');

  const statusMatches = (value, statuses) => statuses.includes(normalizeStatusKey(value));

  const bookingOpenStatuses = ['check-in(inprogress)', 'checkin(inprogress)', 'open', 'confirmed'];
  const bookingPartialStatuses = ['open(inprogress)', 'partial'];
  const bookingClosedStatuses = ['closed', 'completed', 'compleded', 'paid'];
  const productClosedStatuses = ['closed', 'paid', 'completed'];
  const productPartialStatuses = ['partial', 'open(inprogress)'];
  const membershipPartialStatuses = ['partial', 'open(inprogress)'];
  const membershipClosedStatuses = ['closed', 'completed'];

  
  // Notify parent when step changes
  useEffect(() => {
    if (!loading && bookingDataFromAPI && onStepChange) {
      onStepChange(currentStep);
    }
  }, [currentStep, onStepChange, loading, bookingDataFromAPI]);
  const [isDiscardPaymentPopupActive, setIsDiscardPaymentPopupActive] = useState(false);
  const [saleNote, setSaleNote] = useState('');
  const [isSaleNotePopupActive, setIsSaleNotePopupActive] = useState(false);
  const [isCheckoutTopupOpen, setIsCheckoutTopupOpen] = useState(false);
  const [checkoutMembershipFlowType, setCheckoutMembershipFlowType] = useState('topup');
  const [checkoutTopupRefreshKey, setCheckoutTopupRefreshKey] = useState(0);
  const [othersMembershipClient, setOthersMembershipClient] = useState(null);
  const [checkoutTopupCustomer, setCheckoutTopupCustomer] = useState(null);
  const [isOthersMemberTopupFlow, setIsOthersMemberTopupFlow] = useState(false);
  const checkoutPaymentMethodBeforeTopupRef = useRef({ method: '', id: null });
  const checkoutAmountsBeforeTopupRef = useRef({ amountDue: null, amountToCharge: null });
  const checkoutTopupTargetCustomerRef = useRef(null);
  const checkoutStepBeforeTopupRef = useRef('confirmation');
  const homeSidebarSnapshotRef = useRef(null);
  const contextBookingDetailsRef = useRef(null);
  const customerBookingItemsRef = useRef(null);
  const {
    serviceTotal,
    setServiceTotal,
    paymentSummary,
    setPaymentSummary,
    paymentHistory,
    setPaymentHistory,
    amountDue,
    setAmountDue,
    amountToCharge,
    setAmountToCharge,
    reference,
    setReference,
    currentPaymentMethod,
    currentPaymentMethodId,
    setCurrentPaymentMethod,
    setCurrentPaymentMethodTitle,
    setCurrentPaymentMethodId,
    voucherStates,
    setVoucherStates,
    setVoucherDetail,
    voucherDetail,
    loggedUser,
    selectedOutlet,
    selectedOutletData,
    loggedOutletID,
    setBookingsList,
    bookingDetails: contextBookingDetails,
    setBookingDetails: setContextBookingDetails,
    customerBookingItems,
    setCustomerBookingItems,
    setCustomerCreditDetails,
    discountByBookingItemId,
    setDiscountByBookingItemId,
    manualCommissionByBookingItemId,
    setManualCommissionByBookingItemId,
    setClearedEditedCommissionByBookingItemId,
    discountItemToPost,
    setDiscountItemToPost,
    memberRateByBookingItemId,
    setMemberRateByBookingItemId,
    customerData,
    setCustomerData,
    setItemData,
    setIsMembershipApplied,
  } = useGlobalContext();

  contextBookingDetailsRef.current = contextBookingDetails;
  customerBookingItemsRef.current = customerBookingItems;

  // Preserve Home appointment sidebar while sales overlay uses shared context.
  useEffect(() => {
    if (!active) return undefined;

    homeSidebarSnapshotRef.current = {
      bookingDetails: contextBookingDetailsRef.current,
      customerBookingItems: customerBookingItemsRef.current,
    };

    return () => {
      const snapshot = homeSidebarSnapshotRef.current;
      homeSidebarSnapshotRef.current = null;
      if (!snapshot) return;
      setContextBookingDetails(snapshot.bookingDetails);
      setCustomerBookingItems(snapshot.customerBookingItems);
    };
  }, [active, setContextBookingDetails, setCustomerBookingItems]);

  const resetSalesCheckoutState = useCallback(() => {
    setServiceTotal(0);
    setPaymentSummary(DEFAULT_PAYMENT_SUMMARY);
    setPaymentHistory([]);
    setAmountDue(0);
    setAmountToCharge('');
    setReference('');
    setCurrentPaymentMethod('');
    setCurrentPaymentMethodTitle('');
    setCurrentPaymentMethodId(null);
    setVoucherDetail([]);
    setVoucherStates({});
    setDiscountByBookingItemId({});
    setMemberRateByBookingItemId({});
    setManualCommissionByBookingItemId({});
    setClearedEditedCommissionByBookingItemId({});
    setDiscountItemToPost([]);
  }, [
    setAmountDue,
    setAmountToCharge,
    setClearedEditedCommissionByBookingItemId,
    setManualCommissionByBookingItemId,
    setCurrentPaymentMethod,
    setCurrentPaymentMethodTitle,
    setCurrentPaymentMethodId,
    setDiscountByBookingItemId,
    setDiscountItemToPost,
    setMemberRateByBookingItemId,
    setPaymentHistory,
    setPaymentSummary,
    setReference,
    setServiceTotal,
    setVoucherDetail,
    setVoucherStates,
  ]);

  // Close transient UI only. Membership data is cleared when the next sale opens,
  // so closing the sidebar does not discard the currently loaded detail.
  useEffect(() => {
    if (!active) {
      setIsCheckoutTopupOpen(false);
      checkoutTopupTargetCustomerRef.current = null;
      setCheckoutTopupCustomer(null);
      setOthersMembershipClient(null);
      setIsMembershipPaymentDetailOpen(false);
      setSelectedMembershipPayment(null);
      resetSalesCheckoutState();
    }
  }, [active, resetSalesCheckoutState]);

  // Clear stale data when the sidebar opens or switches sale, then let the
  // request below replace it with the newly loaded membership detail.
  useEffect(() => {
    if (!bookingId || !active) return;
    // Reset all data states immediately when bookingId changes
    setBookingDataFromAPI(null);
    setCustomerDataFromAPI(null);
    setBookingDetails(null);
    setMembershipDetail(null);
    setMembershipPaymentDetails([]);
    setCurrentStep('info');
    setSaleNote('');
    setIsDiscardPaymentPopupActive(false);
    setIsSaleNotePopupActive(false);
    setIsCheckoutTopupOpen(false);
    setIsMembershipPaymentDetailOpen(false);
    setSelectedMembershipPayment(null);
  }, [bookingId, active]);

  useEffect(() => {
    setIsMembershipPaymentDetailOpen(false);
    setSelectedMembershipPayment(null);
  }, [membershipDetail?.id]);

  const resolveTopupUserId = () => {
    const targetUserId =
      checkoutTopupTargetCustomerRef.current?.user_id ??
      checkoutTopupCustomer?.id;
    if (targetUserId != null && targetUserId !== '') {
      return targetUserId;
    }
    return bookingDataFromAPI?.user_id ?? bookingDetails?.user?.id ?? null;
  };

  const buildTopupCheckoutCustomer = () => {
    const targetCustomer = membershipClientToTopupCustomer(
      checkoutTopupTargetCustomerRef.current
    );
    if (targetCustomer) {
      return enrichCustomerWithCheckoutPasswordFlag(targetCustomer);
    }

    const userId = resolveTopupUserId();
    const base =
      bookingDetails?.user ||
      customerDataFromAPI ||
      (bookingDataFromAPI && userId
        ? {
            id: userId,
            name: bookingDataFromAPI?.user_name ?? bookingDataFromAPI?.name ?? '',
            contact_number:
              bookingDataFromAPI?.contact_number ?? bookingDataFromAPI?.user_number ?? '',
            email: bookingDataFromAPI?.user_email ?? bookingDataFromAPI?.email ?? '',
          }
        : null);
    return enrichCustomerWithCheckoutPasswordFlag(base);
  };

  const openCheckoutMembershipFlow = async (flowType = 'topup', membershipClient = null) => {
    checkoutStepBeforeTopupRef.current = currentStep || 'confirmation';
    checkoutTopupTargetCustomerRef.current = membershipClient || null;
    setIsOthersMemberTopupFlow(Boolean(membershipClient?.user_id));
    const mappedTopupCustomer = membershipClient
      ? enrichCustomerWithCheckoutPasswordFlag(
          membershipClientToTopupCustomer(membershipClient)
        )
      : null;
    setCheckoutTopupCustomer(mappedTopupCustomer);
    if (membershipClient && !mappedTopupCustomer) {
      showAppToast('error', 'Could not resolve the selected member for top-up.');
      return;
    }

    if (!isCheckoutTopupOpen) {
      checkoutPaymentMethodBeforeTopupRef.current = {
        method: currentPaymentMethod,
        id: currentPaymentMethodId,
      };
      checkoutAmountsBeforeTopupRef.current = {
        amountDue: amountDue,
        amountToCharge: amountToCharge,
      };
    }
    setCheckoutMembershipFlowType(flowType);
    const userId =
      membershipClient?.user_id ??
      mappedTopupCustomer?.id ??
      resolveTopupUserId();
    if (userId) {
      await fetchAndStoreCheckoutCustomerPasswordFlag(userId);
    }
    if (isCheckoutTopupOpen) {
      setIsCheckoutTopupOpen(false);
      window.setTimeout(() => setIsCheckoutTopupOpen(true), 0);
      return;
    }
    setIsCheckoutTopupOpen(true);
  };

  const restoreCheckoutPaymentMethodAfterTopup = () => {
    const { method, id, title } = checkoutPaymentMethodBeforeTopupRef.current;
    setCurrentPaymentMethod(method || '');
    setCurrentPaymentMethodTitle(title || '');
    setCurrentPaymentMethodId(id ?? null);
  };

  const fetchAllOutletProducts = async (outletId) => {
    const allProducts = [];
    let page = 1;
    let lastPage = 1;
    let safetyCounter = 0;

    do {
      const productsResponse = await getProductsList({ pagination: 1, page, outlet: outletId });
      const pageProducts = productsResponse?.data?.data?.list?.service || [];
      const paginationInfo = productsResponse?.data?.data?.list?.pagination || productsResponse?.data?.data?.pagination || {};

      if (Array.isArray(pageProducts) && pageProducts.length > 0) {
        allProducts.push(...pageProducts);
      }

      const apiCurrentPage = Number(paginationInfo.currentPage || page);
      const apiLastPage = Number(paginationInfo.lastPage || apiCurrentPage || 1);

      page = apiCurrentPage + 1;
      lastPage = apiLastPage;
      safetyCounter += 1;
    } while (page <= lastPage && safetyCounter < 50);

    return allProducts;
  };

  /* Kept for HMR / legacy deps; not sent on GET /sales/:id/details (see fetchSaleDetailsForSidebar). */
  const isCalendarExpandedForRequestDate = getOutletExtendedFlagForDate(selectedOutletData?.timing, new Date())
    ? 1
    : 0;

  const getDefaultStepFromBookingStatus = (booking) => {
    const normalizedStatus = String(booking?.status || '').trim().toLowerCase();
    const normalizedPaymentStatus = String(booking?.payment_status || '').trim().toLowerCase();
    const isProductPartialStatus = statusMatches(booking?.status, productPartialStatuses) || statusMatches(booking?.payment_status, productPartialStatuses);
    const isMembershipPartialStatus = statusMatches(booking?.status, membershipPartialStatuses) || statusMatches(booking?.payment_status, membershipPartialStatuses);
    const isBookingOpenOrPartialStatus = statusMatches(booking?.status, [...bookingOpenStatuses, ...bookingPartialStatuses]) || statusMatches(booking?.payment_status, [...bookingOpenStatuses, ...bookingPartialStatuses]);

    if (normalizedStatus === 'no-show' || normalizedStatus === 'no show') {
      return 'info';
    }

    // Product partial should open sale info (with Add Payment), not checkout.
    if (isProductSale && isProductPartialStatus) {
      return 'saleinfo';
    }
    // Membership partial/open-in-progress should open info with Add Payment, not checkout.
    if (isMembershipSale && isMembershipPartialStatus) {
      return 'saleinfo';
    }

    // Keep sales flow in checkout for unpaid/open-like statuses.
    const shouldOpenCheckout =
      normalizedStatus === 'pending' ||
      normalizedStatus === 'partial' ||
      normalizedStatus === 'confirmed' ||
      normalizedStatus === 'holding' ||
      normalizedStatus === 'check-in (in progress)' ||
      normalizedStatus === 'checkin (in progress)' ||
      isBookingOpenOrPartialStatus ||
      normalizedPaymentStatus === 'pending' ||
      normalizedPaymentStatus === 'partial';

    return shouldOpenCheckout ? 'checkout' : 'info';
  };

  const isCheckoutLikeStatus = (statusValue) => {
    const normalizedStatus = String(statusValue || '').trim().toLowerCase();
    return (
      normalizedStatus === 'pending' ||
      normalizedStatus === 'confirmed' ||
      normalizedStatus === 'holding' ||
      normalizedStatus === 'partial' ||
      normalizedStatus === 'check-in (in progress)' ||
      normalizedStatus === 'checkin (in progress)' ||
      statusMatches(statusValue, [...bookingOpenStatuses, ...bookingPartialStatuses])
    );
  };

  const getBookingCustomerEmail = (booking) => booking?.email || booking?.user_email || booking?.user?.email || '';

  const notifyResolvedSaleStatus = (booking) => {
    if (typeof onSaleStatusResolved !== 'function') return;

    const normalizedStatus = String(booking?.status || '').trim().toLowerCase();
    const normalizedPaymentStatus = String(booking?.payment_status || '').trim().toLowerCase();
    const resolvedStatus =
      normalizedStatus === 'completed' ||
      normalizedStatus === 'closed' ||
      normalizedStatus === 'paid' ||
      statusMatches(booking?.status, [...bookingClosedStatuses, ...productClosedStatuses, ...membershipClosedStatuses]) ||
      statusMatches(booking?.payment_status, [...bookingClosedStatuses, ...productClosedStatuses, ...membershipClosedStatuses]) ||
      normalizedPaymentStatus === 'completed' ||
      normalizedPaymentStatus === 'closed' ||
      normalizedPaymentStatus === 'paid' ||
      normalizedStatus === 'cancelled' ||
      normalizedStatus === 'canceled'
        ? 'closed'
        : booking?.status;

    if (resolvedStatus) {
      onSaleStatusResolved(resolvedStatus);
    }
  };

  // Map flat API booking data to the nested structure CheckoutPage expects
  const mapBookingDetailsForContext = (bd) => {
    if (!bd) return null;
    const paymentStatus = bd.sale?.payment_status ?? bd.payment_status ?? "";
    return {
      id: bd.booking_id,
      sale_id: bd.sale_id,
      service_date: bd.service_date,
      service_time: bd.service_time,
      status: bd.status,
      payment_status: paymentStatus,
      sub_total: bd.sub_total,
      deposit: bd.deposit,
      gst_amount: bd.gst_amount,
      cash_rounding: bd.cash_rounding,
      source: bd.source,
      created_by_name: bd.created_by,
      updated_by_name: bd.updated_by,
      booking_created_at: bd.booking_created_at ?? bd.created_at,
      updated_at: bd.updated_at,
      booking_date: bd.booking_date,
      note: bd.note || "",
      parent_sale_id: bd.parent_sale_id,
      child_sale_id: bd.child_sale_id,
      cancel_reason: bd.cancel_reason || "",
      cancel_remark: bd.cancel_remark || "",
      users_notes: bd.users_notes,
      // Needed for cash-voucher verify (service_ids) from Bookings sidebar redeem
      booking_item: bd.booking_item ?? null,
      booking_items: bd.booking_items ?? null,
      services: bd.services ?? null,
      sale: {
        ...(typeof bd.sale === "object" && bd.sale ? bd.sale : {}),
        payment_status: paymentStatus,
      },
      user: {
        id: bd.user_id,
        name: bd.user_name,
        contact_number: bd.contact_number || bd.user_number,
        email: getBookingCustomerEmail(bd),
        membership: bd.membership,
        created_at: bd.customer_joined_at,
        note: bd?.user?.note || bd?.users_notes || "",
        notes: bd?.user?.note || bd?.users_notes || "",
        is_password_set: true,
      },
    };
  };

  // Map flat API customerArray items to the field names CheckoutPage expects
  const mapCustomerArrayForContext = (customerArray) => {
    if (!customerArray) return {};
    const result = {};
    for (const [customerName, bookings] of Object.entries(customerArray)) {
      result[customerName] = (Array.isArray(bookings) ? bookings : []).map((booking) => {
        let additionalInfo = {};
        if (booking.additional) {
          if (typeof booking.additional === 'object') {
            additionalInfo = booking.additional;
          } else {
            try { additionalInfo = JSON.parse(booking.additional); } catch { additionalInfo = {}; }
          }
        }
        return {
          ...booking,
          id: booking.booking_item_id,
          booking_item_id: booking.booking_item_id,
          booking_id: booking.booking_id,
          service: booking.service_name,
          service_name: booking.service_name,
          duration: additionalInfo.duration ?? booking.duration,
          price: booking.price,
          price_rate: booking.price_rate ?? additionalInfo.price_rate ?? null,
          splited: additionalInfo.splited,
          parent_booking_item_id: booking.parent_booking_item_id,
          therapist: booking.therapist_name,
          therapist_name: booking.therapist_name,
          therapist_pager_number: booking.therapist_pager_number,
          gender: booking.therapist_gender,
          therapist_gender: booking.therapist_gender,
          requested_person: additionalInfo.requested_person,
          commission: booking.commission ?? additionalInfo.commission,
          isMemberRateApplied: additionalInfo.isMemberRateApplied ?? booking.isMemberRateApplied,
          isMembershipApplied: additionalInfo.isMembershipApplied ?? booking.isMembershipApplied,
          price_adjustment: booking.price_adjustment ?? additionalInfo.price_adjustment ?? null,
          commission_price_adjustment: booking.commission_price_adjustment ?? additionalInfo.commission_price_adjustment ?? null,
          edited_commission: booking.edited_commission ?? additionalInfo.edited_commission ?? null,
          maximum_commission_price: booking.maximum_commission_price ?? additionalInfo.maximum_commission_price ?? null,
          outlet_id: booking.outlet_id,
          service_id: booking.service_id,
          start_time: booking.start_time,
          service_time: booking.service_time,
          service_at: booking.service_at,
          discount_option: booking.discount_option,
          additional: booking.additional ?? additionalInfo,
          addons: Array.isArray(booking.addons) ? booking.addons : [],
          addons_total: booking.addons_total ?? additionalInfo.addons_total ?? 0,
          commission_addons: booking.commission_addons ?? additionalInfo.commission_addons ?? null,
          room_items: booking.room_items ?? [],
          requested_room: booking.requested_room ?? additionalInfo.requested_room ?? null,
          service_request: booking.service_request ?? additionalInfo.service_request ?? "",
        };
      });
    }
    return result;
  };

  const normalizeServiceBookingData = (serviceBooking) => {
    if (!serviceBooking) return null;

    const user = serviceBooking?.user || serviceBooking || {};
    const outletType = Array.isArray(serviceBooking?.outlet_type) ? serviceBooking.outlet_type[0] : null;
    const bookingType = Array.isArray(serviceBooking?.bookingtype) ? serviceBooking.bookingtype[0] : null;
    return {
      ...serviceBooking,
      booking_id: serviceBooking.id,
      user_id: serviceBooking.user_id ?? user?.id,
      user_name: user?.name || user?.user_name,
      contact_number: user?.contact_number || user?.user_contact_number,
      email: user?.email || user?.user_email,
      customer_joined_at: user?.created_at,
      booking_type: serviceBooking.booking_type_id ?? bookingType?.id,
      outlet_type: outletType?.id ?? serviceBooking.outlet_type_id ?? serviceBooking.outlet_type,
      booking_created_at: serviceBooking.booking_created_at ?? serviceBooking.created_at,
      created_by: serviceBooking.created_by_name,
      updated_by: serviceBooking.updated_by_name,
      users_notes: serviceBooking.users_notes ?? user?.note ?? null,
    };
  };

  const normalizeProductBookingData = (productBooking) => {
    if (!productBooking) return null;
    return {
      ...productBooking,
      booking_id: productBooking.id ?? productBooking.booking_id,
      user_id: productBooking.user_id ?? null,
      user_name: productBooking.user_name ?? '',
      contact_number: productBooking.user_contact_number ?? productBooking.contact_number ?? '',
      email: productBooking.user_email ?? productBooking.email ?? '',
      // Keep a single status source for step and UI decisions.
      status: productBooking.status ?? productBooking.payment_status ?? '',
      payment_status: productBooking.payment_status ?? '',
    };
  };

  const normalizeServiceCustomerArray = (bookingItems, outletId) => {
    if (!bookingItems || typeof bookingItems !== 'object') return {};

    const normalized = {};
    Object.entries(bookingItems).forEach(([customerName, items]) => {
      normalized[customerName] = (Array.isArray(items) ? items : []).map((item) => {
        let existingAdditional = {};
        if (item?.additional) {
          if (typeof item.additional === 'object') {
            existingAdditional = item.additional;
          } else {
            try { existingAdditional = JSON.parse(item.additional); } catch { existingAdditional = {}; }
          }
        }

        return {
          ...item,
          id: item?.id ?? item?.booking_item_id,
          booking_item_id: item?.id ?? item?.booking_item_id,
          booking_id: item?.booking_id,
          service_name: item?.service_name ?? item?.service,
          service: item?.service ?? item?.service_name,
          duration: item?.duration,
          price: item?.price,
          parent_booking_item_id: item?.parent_booking_item_id,
          therapist_name: item?.therapist_name ?? item?.therapist,
          therapist: item?.therapist ?? item?.therapist_name,
          therapist_pager_number: item?.therapist_pager_number,
          therapist_gender: item?.therapist_gender ?? item?.gender,
          gender: item?.gender ?? item?.therapist_gender,
          service_id: item?.service_id,
          outlet_id: item?.outlet_id ?? outletId,
          start_time: item?.start_time,
          service_time: item?.service_time ?? item?.start_time,
          service_at: item?.service_at,
          addons: Array.isArray(item?.addons) ? item.addons : [],
          addons_total: item?.addons_total ?? 0,
          commission_addons: item?.commission_addons ?? null,
          room_items: item?.room_items || [],
          requested_room: item?.requested_room ?? existingAdditional.requested_room ?? null,
          service_request: item?.service_request ?? existingAdditional.service_request ?? "",
          maximum_commission_price: item?.maximum_commission_price ?? existingAdditional.maximum_commission_price ?? null,
          additional: {
            ...existingAdditional,
            requested_person: item?.requested_person ?? existingAdditional.requested_person,
            requested_room: item?.requested_room ?? existingAdditional.requested_room,
            service_request: item?.service_request ?? existingAdditional.service_request,
            splited: item?.splited ?? existingAdditional.splited,
            commission: item?.commission ?? existingAdditional.commission,
            isMemberRateApplied: item?.isMemberRateApplied ?? existingAdditional.isMemberRateApplied,
            isMembershipApplied: item?.isMembershipApplied ?? existingAdditional.isMembershipApplied,
            price_rate: item?.price_rate ?? existingAdditional.price_rate,
            commission_price_adjustment: item?.commission_price_adjustment ?? existingAdditional.commission_price_adjustment,
            edited_commission: item?.edited_commission ?? existingAdditional.edited_commission ?? null,
            maximum_commission_price: item?.maximum_commission_price ?? existingAdditional.maximum_commission_price ?? null,
            addons_total: item?.addons_total ?? existingAdditional.addons_total,
            commission_addons: item?.commission_addons ?? existingAdditional.commission_addons,
          },
        };
      });
    });

    return normalized;
  };

  /** GET /sales/:id/details — booking/product (no selected_date / is_calendar_expansion) */
  const fetchSaleDetailsForSidebar = async () => {
    const queryParams =
      bookingType === 'voucher' ? { type: 'voucher' }: isProductSale  ? { type: 'product' }: {};
    const res = await getSalesDetails(bookingId, queryParams);
    const failureMessage = getSalesDetailsFailureMessage(res);
    if (failureMessage) {
      const error = new Error(failureMessage);
      error.isSaleDetailsError = true;
      throw error;
    }
    const data = res?.data?.data ?? res?.data ?? {};
    return data;
  };

  const applyFetchedSaleDetails = useCallback((data, { overrideBookingId = null } = {}) => {
    const serviceBooking = data?.booking?.bookings?.[0] || data?.bookings?.[0] || data?.bookingData?.[0] || null;
    const bd = isProductSale
      ? normalizeProductBookingData(Array.isArray(data.bookingData) && data.bookingData.length > 0 ? data.bookingData[0] : null)
      : normalizeServiceBookingData(serviceBooking);
    if (bd) {
      bd.booking_date = data?.booking_date || bd.booking_date || serviceBooking?.booking_date;
      if (data?.refund_audit) {
        bd.refund_audit = data.refund_audit;
      }
      if (overrideBookingId) {
        bd.booking_id = overrideBookingId;
        bd.id = overrideBookingId;
      }
    }
    const normalizedCustomerArray = isProductSale
      ? (data.customerArray || {})
      : normalizeServiceCustomerArray(serviceBooking?.booking_item, bd?.outlet_id);

    if (bd && isProductSale) {
      bd.deposit = 0;
    }

    if (bd) {
      setBookingDataFromAPI(bd);
      if (bd.booking_id && onBookingIdResolved) {
        onBookingIdResolved(bd.booking_id);
      }
      notifyResolvedSaleStatus(bd);
      setCustomerDataFromAPI(normalizedCustomerArray);
      setContextBookingDetails(mapBookingDetailsForContext(bd));
      setCustomerBookingItems(mapCustomerArrayForContext(normalizedCustomerArray));

      const noteFromApi =
        data?.sale_note != null && String(data.sale_note).trim() !== ''
          ? String(data.sale_note).trim()
          : (bd?.sales_note || '');
      setSaleNote(noteFromApi);
      if (onSaleNoteChange) {
        onSaleNoteChange(noteFromApi);
      }

      if (isProductSale) {
        setBookingDetails({
          created_at: bd?.created_at,
          booking_created_at: bd?.booking_created_at ?? bd?.created_at,
          created_by_name: bd?.created_by_name,
          updated_at: bd?.updated_at,
          updated_by_name: bd?.updated_by_name,
          booking_date: bd?.booking_date,
          source: bd?.source,
          panel: bd?.panel || 'outlet',
          product_items: bd?.product_items || [],
          sale_id: bd?.sale_id,
          user: {
            id: bd?.user_id,
            name: bd?.user_name,
            contact_number: bd?.contact_number || bd?.user_number,
            email: getBookingCustomerEmail(bd),
            notes: bd?.users_notes || '',
            note: bd?.users_notes || '',
            is_password_set: true,
          },
        });
      }
    }

    return bd;
  }, [
    isProductSale,
    onBookingIdResolved,
    onSaleNoteChange,
    setContextBookingDetails,
    setCustomerBookingItems,
  ]);

  const refreshSaleBookingData = useCallback(async (overrideBookingId = null) => {
    if (!bookingId) return null;
    const data = (await fetchSaleDetailsForSidebar()) || {};
    return applyFetchedSaleDetails(data, { overrideBookingId });
  }, [applyFetchedSaleDetails, bookingId]);

  useEffect(() => {
    if (!active && typeof onSalesSidebarMeta === 'function') {
      onSalesSidebarMeta(null);
    }
  }, [active, onSalesSidebarMeta]);

  useEffect(() => {
    if (!bookingId || !active) return;
    setOthersMembershipClient(null);
    checkoutTopupTargetCustomerRef.current = null;
    setCheckoutTopupCustomer(null);
    const requestId = ++saleDetailsRequestRef.current;
    const isCurrentSaleDetailsRequest = () => requestId === saleDetailsRequestRef.current;
    const fetch = async () => {
      try {
        setSaleDetailsError("");
        setLoading(true);
        if (!isMembershipSale) {
          setMembershipDetail(null);
          if (typeof onSalesSidebarMeta === 'function') {
            onSalesSidebarMeta(null);
          }
        }
        if (isMembershipSale) {
          setMembershipDetail(null);
          const res = await getSalesDetails(bookingId, MEMBERSHIP_SALE_DETAILS_QUERY);
          if (!isCurrentSaleDetailsRequest()) return;
          const failureMessage = getSalesDetailsFailureMessage(res);
          const detail = failureMessage ? null : (res?.data?.data ?? res?.data);
          if (!detail?.id) {
            setSaleDetailsError(failureMessage || SALE_DETAILS_ERROR_FALLBACK);
            setMembershipDetail(null);
            if (typeof onSalesSidebarMeta === 'function') onSalesSidebarMeta(null);
            return;
          }
          setSaleDetailsError("");
          setMembershipDetail(detail);
          const bd = {
            booking_id: detail.id,
            id: detail.id,
            user_id: detail.user_id,
            user_name: detail.user_name,
            user_email: detail.user_email,
            contact_number: detail.user_phone,
            user_number: detail.user_phone,
            outlet_name: detail.outlet_name,
            sale_id: detail.sale_id,
            status: detail.payment_status,
            grand_total: detail.grand_total,
            sub_total: detail.sub_total,
            membership: 1,
          };
          setBookingDataFromAPI(bd);
          if (detail.id && onBookingIdResolved) {
            onBookingIdResolved(detail.id);
          }
          setCustomerDataFromAPI({});
          setContextBookingDetails({
            id: detail.id,
            status: detail.payment_status,
            payment_status: detail.payment_status,
            sale_id: detail.sale_id,
            sale: {
              payment_status: detail.payment_status,
            },
            user: {
              id: detail.user_id,
              name: detail.user_name,
              email: detail.user_email,
              contact_number: detail.user_phone,
              is_password_set: true,
            },
            created_at: detail.created_at,
            updated_at: detail.updated_at,
            booking_date: detail.booking_date,
            created_by_name: detail.created_by_name,
            updated_by_name: detail.updated_by_name,
          });
          if (detail.user_id) {
            fetchAndStoreCheckoutCustomerPasswordFlag(detail.user_id);
          }
          setCustomerBookingItems({});
          const noteFromApi =
            detail.sale_note != null && String(detail.sale_note).trim() !== ''
              ? String(detail.sale_note).trim()
              : '';
          setSaleNote(noteFromApi);
          if (onSaleNoteChange) onSaleNoteChange(noteFromApi);
          setBookingDetails({
            created_at: detail.created_at,
            updated_at: detail.updated_at,
            booking_date: detail.booking_date,
            created_by_name: detail.created_by_name,
            updated_by_name: detail.updated_by_name,
            panel: 'outlet',
            sale_id: detail.sale_id,
            user: {
              id: detail.user_id,
              name: detail.user_name,
              email: detail.user_email,
              contact_number: detail.user_phone,
              is_password_set: true,
            },
          });
          notifyResolvedSaleStatus(bd);
          setCurrentStep('info');
          if (typeof onSalesSidebarMeta === 'function') {
            const actionLabel = (detail.action_label || '').trim();
            const label = actionLabel.toLowerCase() === 'top-up'? 'Sale': actionLabel || 'Membership';
            const rawSt = String(detail.payment_status || '').toLowerCase();
            const st = rawSt === 'paid' ? 'closed' : rawSt === 'partial' ? 'Open - In Progress' : rawSt;
            const titleId = detail.sale_id ?? detail.id;
            onSalesSidebarMeta({
              title: `${label} #${titleId}${st ? ` (${st})` : ''}`,
              customerEmail: detail.user_email || '',
              membershipDetail: detail,
              childSaleId: detail.child_sale_id || null,
            });
          }
          const membershipSaleId = detail.sale_id ?? detail.id;
          if (membershipSaleId) {
            try {
              const paymentRes = await getPaymentDetails(membershipSaleId, { sale_id: membershipSaleId });
              if (!isCurrentSaleDetailsRequest()) return;
              const paymentData = paymentRes?.data?.data || paymentRes?.data || [];
              setMembershipPaymentDetails(Array.isArray(paymentData) ? paymentData : []);
            } catch (paymentError) {
              if (!isCurrentSaleDetailsRequest()) return;
              setMembershipPaymentDetails([]);
              console.error('Failed to load membership payment details', paymentError);
            }
          }
          return;
        }

        const data = (await fetchSaleDetailsForSidebar()) || {};
        if (!isCurrentSaleDetailsRequest()) return;
        const serviceBooking = data?.booking?.bookings?.[0] || data?.bookings?.[0] || data?.bookingData?.[0] || null;
        const bd = isProductSale
          ? normalizeProductBookingData(Array.isArray(data.bookingData) && data.bookingData.length > 0 ? data.bookingData[0] : null)
          : normalizeServiceBookingData(serviceBooking);
        if (bd) {
          bd.booking_date = data?.booking_date || bd.booking_date || serviceBooking?.booking_date;
          if (data?.refund_audit) {
            bd.refund_audit = data.refund_audit;
          }
        }
        if (!bd) {
          setSaleDetailsError(SALE_DETAILS_ERROR_FALLBACK);
          setBookingDataFromAPI(null);
          return;
        }
        setSaleDetailsError("");
        const normalizedCustomerArray = isProductSale
          ? (data.customerArray || {})
          : normalizeServiceCustomerArray(serviceBooking?.booking_item, bd?.outlet_id);
        const mappedContextBookingDetails = mapBookingDetailsForContext(bd);
        const mappedCustomerBookingItems = mapCustomerArrayForContext(normalizedCustomerArray);
        // Ensure deposit is 0 for product sales
        if (bd && isProductSale) {
          bd.deposit = 0; // Force deposit to 0 for product sales
        }
        if (isServiceBooking) {
          resetSalesCheckoutState();
        }
        setBookingDataFromAPI(bd);
        if (bd?.booking_id) {
          if (onBookingIdResolved) onBookingIdResolved(bd.booking_id);
        }
        notifyResolvedSaleStatus(bd);
        setCustomerDataFromAPI(normalizedCustomerArray);
        setContextBookingDetails(mappedContextBookingDetails);
        setCustomerBookingItems(mappedCustomerBookingItems);

        // Seed discount / member-rate overrides from the fresh API response.
        // Live local overrides (including explicit null = user cleared) win.
        if (isServiceBooking && mappedCustomerBookingItems && typeof mappedCustomerBookingItems === 'object') {
          const { seededDiscounts, seededMemberRates } = buildSeededPricingOverrides(mappedCustomerBookingItems);
          if (Object.keys(seededDiscounts).length > 0) {
            setDiscountByBookingItemId((prev) => mergePricingOverrideMap(prev, seededDiscounts));
          }
          if (Object.keys(seededMemberRates).length > 0) {
            setMemberRateByBookingItemId((prev) => mergePricingOverrideMap(prev, seededMemberRates));
          }
        }
        // Do not rewrite GlobalContext bookingsList here — Home calendar shares that state.
        // Refund responses can return sale_note at the top level while the nested booking has sales_note.
        const noteFromApi =
          data?.sale_note != null && String(data.sale_note).trim() !== ''
            ? String(data.sale_note).trim()
            : (bd?.sales_note || '');
        setSaleNote(noteFromApi);
        if (onSaleNoteChange) {
          onSaleNoteChange(noteFromApi);
        }

        setBookingDetails({
          created_at: bd?.created_at,
          booking_created_at: bd?.booking_created_at ?? bd?.created_at,
          created_by_name: bd?.created_by_name,
          updated_at: bd?.updated_at,
          updated_by_name: bd?.updated_by_name,
          booking_date: bd?.booking_date,
          source: bd?.source,
          panel: bd?.panel || 'outlet',
          product_items: bd?.product_items || [],
          sale_id: bd?.sale_id,
          user: {
            id: bd?.user_id,
            name: bd?.user_name,
            contact_number: bd?.contact_number || bd?.user_number,
            email: getBookingCustomerEmail(bd),
            notes: bd?.users_notes || "",
            note: bd?.users_notes || "",
            is_password_set: true,
          },
        });

        if (bd?.user_id) {
          fetchAndStoreCheckoutCustomerPasswordFlag(bd.user_id);
        }

        setCurrentStep(initialStep || getDefaultStepFromBookingStatus(bd));
      } catch (e) {
        if (!isCurrentSaleDetailsRequest()) return;
        // eslint-disable-next-line no-console
        console.error('❌ Failed to fetch booking details from sales sidebar', e);
        setSaleDetailsError(
          e?.isSaleDetailsError && e?.message ? e.message : SALE_DETAILS_ERROR_FALLBACK
        );
        setBookingDataFromAPI(null);
        if (isMembershipSale) {
          setMembershipDetail(null);
        }
      } finally {
        if (isCurrentSaleDetailsRequest()) {
          setLoading(false);
        }
      }
    };
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid refetch loops; parent callbacks omitted intentionally
  }, [active, bookingId, bookingType, isCalendarExpandedForRequestDate, isMembershipSale, isProductSale, saleDetailsAttempt]);

  useEffect(() => {
    const handleCustomerNotesUpdated = (event) => {
      const updatedCustomerId = event?.detail?.customerId;
      const updatedNotes = event?.detail?.notes ?? "";
      if (!updatedCustomerId) return;

      setBookingDataFromAPI((prev) => {
        if (!prev || Number(prev?.user_id) !== Number(updatedCustomerId)) return prev;
        const updated = {
          ...prev,
          users_notes: updatedNotes,
          user: {
            ...(prev.user || {}),
            note: updatedNotes,
            notes: updatedNotes,
          },
        };

        // Keep CheckoutPage data source in sync
        setContextBookingDetails(mapBookingDetailsForContext(updated));
        return updated;
      });

      setBookingDetails((prev) => {
        if (!prev) return prev;
        const prevUserId = prev?.user?.id;
        if (Number(prevUserId) !== Number(updatedCustomerId)) return prev;
        return {
          ...prev,
          user: {
            ...(prev.user || {}),
            note: updatedNotes,
            notes: updatedNotes,
          },
        };
      });
    };

    window.addEventListener('customerNotesUpdated', handleCustomerNotesUpdated);
    return () => {
      window.removeEventListener('customerNotesUpdated', handleCustomerNotesUpdated);
    };
  }, [setContextBookingDetails]);

  // Keep sale sidebar customer name in sync when edited from CustomerDetailsModal
  useEffect(() => {
    const handleCustomerUpdated = (event) => {
      const updatedCustomer = event?.detail?.customer;
      if (!updatedCustomer?.id) return;
      const newName = updatedCustomer.name;
      if (!newName) return;

      setBookingDataFromAPI((prev) => {
        if (!prev || Number(prev?.user_id) !== Number(updatedCustomer.id)) return prev;
        const updated = { ...prev, user_name: newName };
        setContextBookingDetails(mapBookingDetailsForContext(updated));
        return updated;
      });
    };

    window.addEventListener('customerUpdated', handleCustomerUpdated);
    return () => window.removeEventListener('customerUpdated', handleCustomerUpdated);
  }, [setContextBookingDetails]);

  // Listen for note addition and void sale events from SideBarRight
  useEffect(() => {
    const handleDataRefresh = async (event) => {
      if (!bookingId) return;
      if (event?.type === 'voidSaleCompleted') {
        triggerHomeCalendarRefreshIfMounted(setBookingsList);
      }
      if (event?.type === 'salesPaymentSuccess') {
        const eventSaleId = event?.detail?.saleId ?? event?.detail?.bookingId ?? event?.detail?.id;
        if (eventSaleId != null && Number(eventSaleId) !== Number(bookingId)) return;
      }
      try {
        if (isMembershipSale) {
          const res = await getSalesDetails(bookingId, MEMBERSHIP_SALE_DETAILS_QUERY);
          const detail = res?.data?.data ?? res?.data;
          if (!detail?.id) return;

          setMembershipDetail(detail);
          notifyResolvedSaleStatus({
            status: detail.payment_status,
            payment_status: detail.payment_status,
            payment_status_id: detail.payment_status_id,
          });
          const noteFromApi =
            detail.sale_note != null && String(detail.sale_note).trim() !== ''
              ? String(detail.sale_note).trim()
              : '';
          setSaleNote(noteFromApi);
          if (onSaleNoteChange) {
            onSaleNoteChange(noteFromApi);
          }
          if (typeof onSalesSidebarMeta === 'function') {
            const actionLabel = (detail.action_label || '').trim();
            const label = actionLabel.toLowerCase() === 'top-up' ? 'Sale' : actionLabel || 'Membership';
            const rawSt = String(detail.payment_status || '').toLowerCase();
            const st = rawSt === 'paid' ? 'closed' : rawSt === 'partial' ? 'Open - In Progress' : rawSt;
            const titleId = detail.sale_id ?? detail.id;
            onSalesSidebarMeta({
              title: `${label} #${titleId}${st ? ` (${st})` : ''}`,
              customerEmail: detail.user_email || '',
              membershipDetail: detail,
              childSaleId: detail.child_sale_id || null,
            });
          }
          const membershipSaleId = detail.sale_id ?? detail.id;
          if (membershipSaleId) {
            try {
              const paymentRes = await getPaymentDetails(membershipSaleId, { sale_id: membershipSaleId });
              const paymentData = paymentRes?.data?.data || paymentRes?.data || [];
              setMembershipPaymentDetails(Array.isArray(paymentData) ? paymentData : []);
            } catch (paymentError) {
              console.error('Failed to refresh membership payment details', paymentError);
            }
          }
          return;
        }

        const data = (await fetchSaleDetailsForSidebar()) || {};
        applyFetchedSaleDetails(data);
      } catch (e) {
        console.error('Failed to refresh booking details after note addition', e);
      }
    };

    window.addEventListener('salesNoteAdded', handleDataRefresh);
    window.addEventListener('voidSaleCompleted', handleDataRefresh);
    window.addEventListener('salesPaymentSuccess', handleDataRefresh);
    return () => {
      window.removeEventListener('salesNoteAdded', handleDataRefresh);
      window.removeEventListener('voidSaleCompleted', handleDataRefresh);
      window.removeEventListener('salesPaymentSuccess', handleDataRefresh);
    };
  }, [bookingId, bookingType, isMembershipSale, isProductSale, isCalendarExpandedForRequestDate, applyFetchedSaleDetails, setBookingsList]);

  const handleDiscardPayment = () => {
    resetSalesCheckoutState();
    // Explicitly clear discount & member-rate overrides for this booking
    setDiscountByBookingItemId({});
    setMemberRateByBookingItemId({});
    setVoucherDetail([]);
    setVoucherStates({});
    // Restore any mutated prices on the local customerData copy
    if (customerDataFromAPI) {
      const cloned = { ...customerDataFromAPI };
      Object.values(cloned).forEach((bookings) => {
        bookings.forEach((svc) => {
          if (svc.originalPrice !== undefined) {
            svc.price = svc.originalPrice;
            delete svc.originalPrice;
          }
        });
      });
      setCustomerDataFromAPI(cloned);
    }
    setIsDiscardPaymentPopupActive(false);
    setCurrentStep(getDefaultStepFromBookingStatus(bookingDataFromAPI));
  };

  const handleConfirmPayment = async ({
    method,
    amount,
    partial,
    reference,
    payment_method_id,
    skip_password,
    skip_password_reason,
    selectedUserId,
    membershipId,
    voucher_code,
    vouchers,
  }) => {
    try {
      let paymentItem;
      let response;
      const parsedAmount = parseFloat(amount);
      const isCashPaymentMethod = method?.toUpperCase() === 'CASH';
      const isCashVoucher = method === 'cash_voucher' ||
        String(method || '').toLowerCase().replace(/[\s-]+/g, '_') === 'cash_voucher';
      const payableAmount = isCashPaymentMethod && !isNaN(parsedAmount)
        ? roundCashAmount(parsedAmount)
        : parsedAmount;
      const vouchersForPayload = Array.isArray(vouchers) && vouchers.length > 0 ? vouchers : voucherDetail;
      const minifiedVouchers =!isCashVoucher && vouchersForPayload?.length > 0 ? JSON.stringify(vouchersForPayload) : null;
      const resolvedSaleId =bookingDataFromAPI?.sale_id || null;
      const cashVoucherFields = isCashVoucher ? { sale_id: resolvedSaleId,voucher_code: String(voucher_code || '').trim(),  }: { vouchers: minifiedVouchers };

      // For product sales, use product payment API with specific payload structure
      if (isProductSale) {
        const items = buildProductPaymentItemsFromBookingData(bookingDataFromAPI);

        // Check if this payment will complete the amount due
        let currentAmountDue = parseFloat(amountDue);
        const paymentAmount = !isNaN(payableAmount) ? payableAmount : 0;
         if (isCashPaymentMethod && !isNaN(currentAmountDue)) {
          currentAmountDue = roundCashAmount(currentAmountDue);
        }
        const remainingAfterPayment = currentAmountDue - paymentAmount;
        const isPaymentComplete = remainingAfterPayment <= 0;
        const outletId = selectedOutlet || loggedOutletID;
        // Get outlet data - use localOutletData (fetched) or selectedOutletData (from context)
        const outletData = selectedOutletData;
        const outletType = outletData?.outlet_type_id;

        paymentItem = {
          outlet: outletId,
          outlet_type: outletType,
          customer: bookingDataFromAPI?.customer_id || bookingDataFromAPI?.user_id,
          booking_id: bookingDataFromAPI?.booking_id, // Required for partial payments
          items: items,
          sub_total: parseFloat(paymentSummary.subtotal || bookingDataFromAPI?.sub_total || 0),
          grand_total: parseFloat(bookingDataFromAPI?.grand_total || 0),
          currency: 'SGD',
          amount: !isNaN(payableAmount) ? payableAmount : amount,
          method: method,
          payment_type: method || "payatstore",
          payment_status: isPaymentComplete ? 2 : 3, // 2 = Full payment, 3 = Partial payment
          amount_completed: isPaymentComplete ? 1 : 0, // 1 if payment completes, 0 if partial
          service_at: new Date().toLocaleDateString('en-GB') + " " + new Date().toLocaleTimeString('en-GB', {hour: '2-digit', minute:'2-digit'}),
          note: bookingDataFromAPI?.sales_note || '',
          newsletter: 0,
          panel: 'outlet',
          type: resolveCheckoutSaleType(bookingType, bookingDataFromAPI),
          reference: reference || '',
          payment_method_id: payment_method_id,
          updated_by: loggedUser,
          vouchers: voucherDetail?.length ? JSON.stringify(voucherDetail) : null,
          ...(method === 'cashless' ||
          method === 'other_membership'
            ? { skip_password: skip_password, skip_password_reason: skip_password_reason }
            : {}),
          ...(selectedUserId ? { membership_user_id: selectedUserId } : {}),
          ...buildMembershipIdPaymentFields(membershipId),
        };
        
        // console.log('Product sale payment API payload:', paymentItem);
        // console.log("paymentItem", paymentItem);
        response = await postProductPayment(paymentItem);

      } else if (isMembershipSale && membershipDetail) {
        // For membership sales, use membership payment API
        const membershipIdForPayment = membershipDetail?.membership_id || membershipDetail?.id;
        paymentItem = {
          membership_transaction_id: membershipDetail?.id,
          amount: !isNaN(payableAmount) ? payableAmount : amount,
          payment_method_id: payment_method_id,
          payment_reference: String(reference || '').trim() || '',
          panel: 'outlet',
        };
        response = await postMembershipTransactionPayment(membershipIdForPayment, paymentItem);
      } else {
        // For service bookings, use existing service payment API
        paymentItem = {
          id: bookingDataFromAPI?.booking_id || null,
          booking_id: bookingDataFromAPI?.booking_id || null,
          amount: !isNaN(payableAmount) ? payableAmount : amount,
          tip: parseFloat(paymentSummary.tip || 0),
          currency: 'SGD',
          sub_total: parseFloat(paymentSummary.subtotal || 0),
          method: method,
          payment_method_id: payment_method_id,
          panel: 'outlet',
          reference: reference || '',
          deposit: bookingDataFromAPI?.deposit || 0,
          updated_by: loggedUser,
          ...cashVoucherFields,
          ...(method === 'cashless' ||
          method === 'other_membership'
            ? { skip_password: skip_password, skip_password_reason: skip_password_reason }
            : {}),
          ...(selectedUserId ? { membership_user_id: selectedUserId } : {}),
          ...buildMembershipIdPaymentFields(membershipId),
        };
        
        // console.log('Service booking payment API payload:', paymentItem);
        response = await postpaymentData(paymentItem);
      }

      // Handle response — normalize success check across different API shapes
      const isSuccess = response?.data?.success || response?.success;
      if (isSuccess) {
        setCheckoutTopupRefreshKey((previousValue) => previousValue + 1);
        const responseData = response?.data?.data || response?.data || {};
        const paymentStatus = responseData?.status;
        const saleIdFromResponse = extractSaleIdFromPaymentResponse(
          response,
          bookingDataFromAPI?.booking_id || bookingDataFromAPI?.id
        );
        const normalizedPaymentStatus = String(paymentStatus || '').toLowerCase();
        const isFullPayment = normalizedPaymentStatus === "completed" || normalizedPaymentStatus === "paid";
        
        const newHistory = [
          ...paymentHistory,
          { method, amount, id: bookingDataFromAPI?.booking_id || null },
        ];
        setPaymentHistory(newHistory);
        setPaymentSummary((prev) => ({ ...prev, payments: newHistory }));

        let currentAmountDue = parseFloat(amountDue);
        if (isCashPaymentMethod && !isNaN(currentAmountDue)) {
          currentAmountDue = roundCashAmount(currentAmountDue);
        }
        let updatedDue = currentAmountDue - (!isNaN(payableAmount) ? payableAmount : 0);
        if (isCashPaymentMethod && !isNaN(updatedDue)) {
          updatedDue = roundCashAmount(updatedDue);
        }
        const newDue = updatedDue < 0 ? 0 : updatedDue;
        setAmountDue(newDue.toFixed(2));

        if (newDue > 0) {
          // Partial payment - stay on payment page for next payment
          try {
            await refreshSaleBookingData(saleIdFromResponse);
            if (saleIdFromResponse && onBookingIdResolved) {
              onBookingIdResolved(saleIdFromResponse);
            }
          } catch (refreshError) {
            console.error('Failed to refresh booking details after partial payment', refreshError);
          }

          try {
            window.dispatchEvent(new CustomEvent('salesPaymentSuccess', {
              detail: {
                bookingId: saleIdFromResponse || bookingDataFromAPI?.booking_id || bookingDataFromAPI?.id,
                saleId: saleIdFromResponse || bookingDataFromAPI?.booking_id || bookingDataFromAPI?.id,
              },
            }));
          } catch {}
          if (isMembershipSale) {
            try { window.dispatchEvent(new CustomEvent('refreshClientList')); } catch {}
          }
          if (isMembershipSale) {
            // For membership, stay on payment step to allow further payments
            setCurrentStep('payment');
            return response;
          }
          setCurrentStep('payment');
        } else if (isMembershipSale) {
          // Membership full payment — refresh and go back to info
          setCurrentPaymentMethod('');
          setCurrentPaymentMethodTitle('');
          if (typeof onSaleStatusResolved === 'function') {
            onSaleStatusResolved('closed');
          }
          setMembershipDetail((prev) => (prev ? { ...prev, payment_status: 'closed' } : prev));
          try {
            window.dispatchEvent(new CustomEvent('salesPaymentSuccess', {
              detail: {
                bookingId: saleIdFromResponse || bookingDataFromAPI?.booking_id || bookingDataFromAPI?.id,
                saleId: saleIdFromResponse || bookingDataFromAPI?.booking_id || bookingDataFromAPI?.id,
              },
            }));
          } catch {}
          try { window.dispatchEvent(new CustomEvent('refreshClientList')); } catch {}
          const customerId = membershipDetail?.user_id || bookingDataFromAPI?.user_id;
          if (customerId && Number(customerData?.id) === Number(customerId)) {
            try {
              const customerResponse = await getCustomerDetails(customerId);
              const refreshedCustomer = customerResponse?.data?.data;
              if (refreshedCustomer) {
                const rawMembershipStatus =
                  refreshedCustomer?.membership_detail?.status_label ??
                  refreshedCustomer?.membership_detail?.status;
                const membership = rawMembershipStatus != null && String(rawMembershipStatus).trim() !== ''
                  ? Number(['active', '1'].includes(String(rawMembershipStatus).trim().toLowerCase()))
                  : Number(refreshedCustomer.membership === true || Number(refreshedCustomer.membership) === 1);
                const updatedCustomer = { ...refreshedCustomer, id: customerId, membership };
                setCustomerData(updatedCustomer);
                setItemData((previous) => ({ ...previous, membership }));
                setIsMembershipApplied(Boolean(membership));
                window.dispatchEvent(new CustomEvent('customerUpdated', {
                  detail: { customer: updatedCustomer },
                }));
              }
            } catch (customerRefreshError) {
              console.error('Failed to refresh customer after membership payment', customerRefreshError);
            }
          }
          const paymentSuccessMessage =
            response?.message || response?.data?.message || 'Payment completed successfully!';
          showAppToast('success', paymentSuccessMessage);
          triggerHomeCalendarRefreshIfMounted(setBookingsList);
          // Re-fetch membership detail and payment transactions
          try {
            const res = await getSalesDetails(bookingId, MEMBERSHIP_SALE_DETAILS_QUERY);
            const detail = res?.data?.data ?? res?.data;
            if (detail?.id) {
              setMembershipDetail({ ...detail, payment_status: 'closed' });
              const membershipSaleId = detail?.sale_id || detail?.id;
              if (membershipSaleId) {
                const paymentRes = await getPaymentDetails(membershipSaleId, { sale_id: membershipSaleId });
                const paymentData = paymentRes?.data?.data || paymentRes?.data || [];
                setMembershipPaymentDetails(Array.isArray(paymentData) ? paymentData : []);
              }
            }
          } catch {}
          setCurrentStep('info');
          return response;
        } else {
          setCurrentPaymentMethod('');
          setCurrentPaymentMethodTitle('');
          // Call outlet availability check API if full payment is successful and it's a product sale
          if ((isFullPayment || newDue <= 0) && isProductSale && bookingDataFromAPI?.product_items) {
            try {
              // Use selected outlet from context (since we only show/sell products from selected outlet)
              const outletId = selectedOutlet || loggedOutletID || bookingDataFromAPI?.outlet_id;
              
              if (!outletId) {
                console.warn('No outlet ID available for outlet availability check');
                return;
              }
              
              // Fetch products list to get outlet_availability_id
              const productsList = await fetchAllOutletProducts(outletId);
              
              // Build payload by looking up outlet_availability_id from products API
              const productsWithAvailability = bookingDataFromAPI.product_items
                .map(item => {
                  const productId = item.product || item.product_id || item.id;
                  if (!productId) {
                    console.warn('No product ID found in item:', item);
                    return null;
                  }
                  
                  // Find the product in the products list
                  const product = productsList.find(p => Number(p.id) === Number(productId));
                  if (!product) {
                    console.warn('Product not found in products list, product_id:', productId);
                    return null;
                  }
                  
                  if (!product.outlet_availabilities || product.outlet_availabilities.length === 0) {
                    console.warn('Product has no outlet_availabilities, product_id:', productId);
                    return null;
                  }
                  
                  // Find the matching outlet_availability by outlet_id (selected outlet)
                  const outletAvailability = product.outlet_availabilities.find(
                    oa => Number(oa.outlet_id) === Number(outletId)
                  );
                  
                  if (!outletAvailability || !outletAvailability.id) {
                    console.warn('No matching outlet_availability found, product_id:', productId, 'outlet_id:', outletId);
                    return null;
                  }
                  
                  return {
                    outlet_availability_id: outletAvailability.id,
                    quantity: item.quantity || item.qty || 1
                  };
                })
                .filter(item => item !== null); // Remove null entries
              
              if (productsWithAvailability.length > 0) {
                const availabilityPayload = {
                  products: productsWithAvailability
                };
                
                await checkOutletAvailability(availabilityPayload);
              } else {
                console.warn('No products with outlet_availability_id found. Product items:', bookingDataFromAPI.product_items);
              }
            } catch (error) {
              console.error('Error checking outlet availability:', error);
              // Don't show error to user as this is a background operation
            }
          }
          
          // Refresh booking to show sale info after payment
          setLoading(true);
          try {
            await refreshSaleBookingData();
          } finally {
            setLoading(false);
            setCurrentStep('saleinfo');
            if (typeof onSaleStatusResolved === 'function') {
              onSaleStatusResolved('closed');
            }
            const paymentSuccessMessage =
              response?.data?.message ||
              response?.message ||
              (isProductSale ? 'Product payment completed successfully!' : 'Payment completed successfully!');
            showAppToast('success', paymentSuccessMessage);
            try {
              window.dispatchEvent(new CustomEvent('salesPaymentSuccess', {
                detail: {
                  bookingId: saleIdFromResponse || bookingDataFromAPI?.booking_id || bookingDataFromAPI?.id,
                  saleId: saleIdFromResponse || bookingDataFromAPI?.booking_id || bookingDataFromAPI?.id,
                },
              }));
            } catch {}
            triggerHomeCalendarRefreshIfMounted(setBookingsList);
          }
        }
      } else {
        return response;
      }
      return response;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Payment failed', e);
      const referenceErrors = e?.response?.data?.errors?.reference;
      if (Array.isArray(referenceErrors) && referenceErrors.length > 0) {
        return e?.response;
      }
      if (typeof referenceErrors === 'string' && referenceErrors.trim()) {
        return e?.response;
      }
      try { window.dispatchEvent(new CustomEvent('salesPaymentSuccess')); } catch {}
      showAppToast('error', 'Payment failed');
      return e?.response || { success: false, message: e?.message || 'Payment failed' };
    }
  };

  // Sale Note Functions
  const showAddNotePopup = () => {
    setIsSaleNotePopupActive(true);
  };

  const closeSaleNotePopup = () => {
    setIsSaleNotePopupActive(false);
  };

  const addSaleNoteHandler = async (note) => {
    try {
      const params = {
        id: bookingId,
        panel: "outlet",
        note: note.trim(),
        updated_by: loggedUser
      };

      const response = await addSaleNote(params);
      if (response?.data?.success) {
        setSaleNote(note.trim());
        if (isMembershipSale) {
          setMembershipDetail((prev) => (prev ? { ...prev, sale_note: note.trim() } : null));
        }
        if (onSaleNoteChange) {
          onSaleNoteChange(note.trim());
        }
        setIsSaleNotePopupActive(false);
        showAppToast('success', 'Note added successfully!');
      } else {
        showAppToast('error', 'Failed to add note. Please try again.');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      showAppToast('error', 'Error adding note. Please try again.');
    }
  };

  const showActionNotAvailableInSalesSidebar = () => {
    showAppToast('info', 'This action is not available from sales sidebar.');
  };

  const handleBack = () => {
    const normalizedStatus = String(bookingDataFromAPI?.status || '').toLowerCase();
    if (isProductSale) {
      setCurrentStep('saleinfo');
      return;
    }
    setCurrentStep(isCheckoutLikeStatus(normalizedStatus) ? 'checkout' : 'info');
  };

  const handleCancelPayment = () => {
    if(isProductSale) {
      setCurrentStep('saleinfo');
    } else {
      setCurrentStep('checkout');
    }
  };

  const handleConfirmationReload = (failedResponse) => {
    const isAlreadyCompleted = isBookingAlreadyCompletedResponse(failedResponse);
    setDiscountByBookingItemId({});
    setMemberRateByBookingItemId({});
    setManualCommissionByBookingItemId({});
    setClearedEditedCommissionByBookingItemId({});
    setDiscountItemToPost([]);
    setCurrentPaymentMethod('');
    setCurrentPaymentMethodTitle('');
    refreshSaleBookingData().catch((error) => {
      console.error('Failed to reload booking details', error);
    });
    if (isAlreadyCompleted && typeof onSaleStatusResolved === 'function') {
      onSaleStatusResolved('closed');
    }
    setCurrentStep(isProductSale || isAlreadyCompleted ? 'saleinfo' : 'checkout');
  };
  logger.log("bookingDataFromAPI", bookingDataFromAPI);

  const saleDetailsErrorView = (
    <SidebarLoadError
      className={sidebarStyles.saleDetailsError}
      title="Unable to load this sale"
      message={saleDetailsError}
      onReload={() => setSaleDetailsAttempt((attempt) => attempt + 1)}
    />
  );

  return (
    <>
      {loading ? (
        <Loader />
      ) : saleDetailsError && ((isMembershipSale && !membershipDetail) || !bookingDataFromAPI) ? (
        saleDetailsErrorView
      ) : isMembershipSale && membershipDetail && currentStep === 'info' ? (
        <MembershipSaleSidebarInfo
          detail={membershipDetail}
          paymentDetails={membershipPaymentDetails}
          saleNote={saleNote}
          showAddNotePopup={showAddNotePopup}
          onPaymentViewDetails={(payment) => {
            setSelectedMembershipPayment(payment);
            setIsMembershipPaymentDetailOpen(true);
          }}
          onAddPayment={
            statusMatches(membershipDetail?.payment_status, membershipPartialStatuses)
              ? () => {
                  const total = parseFloat(membershipDetail?.grand_total || membershipDetail?.sub_total || 0);
                  setServiceTotal(total);
                  setCurrentStep('payment');
                }
              : undefined
          }
          onOpenRelatedSale={onOpenRelatedSale}
        />
      ) : isMembershipSale && !membershipDetail ? (
        saleDetailsError ? saleDetailsErrorView : (
          <div className="p-4 text-center text-muted">Unable to load this membership sale.</div>
        )
      ) : !bookingDataFromAPI ? (
        <Loader />
      ) : (
        (() => {
          switch (currentStep) {
            case 'checkout':
              return (
                <>
                  <CheckoutPage
                    onBack={() => setCurrentStep(getDefaultStepFromBookingStatus(bookingDataFromAPI))}
                    onNext={() => setCurrentStep('payment')}
                    discardPaymentPopup={() => {
                      if (typeof onCloseSidebar === 'function') {
                        resetSalesCheckoutState();
                        onCloseSidebar();
                        return;
                      }
                      setIsDiscardPaymentPopupActive(true);
                    }}
                    hideHeader={true}
                    isSalesFlow={true}
                    showAddNotePopup={showAddNotePopup}
                    saleNote={saleNote}
                    disableServiceListHover={true}
                  />
                  {isDiscardPaymentPopupActive && !onCloseSidebar && (
                    <DiscardPaymentPopup
                      onClose={() => setIsDiscardPaymentPopupActive(false)}
                      onConfirm={handleDiscardPayment}
                    />
                  )}
                </>
              );
            case 'payment':
              return (
                <PaymentPage
                  bookingDataFromAPI={bookingDataFromAPI}
                  customerDataFromAPI={customerDataFromAPI}
                  onBack={() => {
                    if (isMembershipSale && membershipDetail) {
                      setCurrentStep('info');
                    } else {
                      handleBack();
                    }
                  }}
                  onNext={() => setCurrentStep('confirmation')}
                  onReload={handleConfirmationReload}
                  completeBooking={handleConfirmPayment}
                  setCurrentStep={setCurrentStep}
                  hideHeader={true}
                  isProductSale={isProductSale}
                  isMembershipPayment={isMembershipSale}
                  gstRate={isMembershipSale ? 0 : 9}
                  onTopUpClick={openCheckoutMembershipFlow}
                  customerCreditRefreshKey={checkoutTopupRefreshKey}
                  paymentMethodsRefreshKey={checkoutTopupRefreshKey}
                  creditFetchUserId={bookingDataFromAPI?.user_id || bookingDetails?.user?.id || null}
                />
              );
            case 'confirmation':
              return (
                <ConfirmationPage
                  summary={paymentSummary}
                  onBack={() => setCurrentStep('payment')}
                  onReload={handleConfirmationReload}
                  onConfirm={handleConfirmPayment}
                  onCancel={handleCancelPayment}
                  bookingID={bookingDataFromAPI?.booking_id || null}
                  hideHeader={true}
                  isProductSale={isProductSale}
                  membershipListUserId={bookingDataFromAPI?.user_id || bookingDetails?.user?.id || null}
                  selectedOthersMembershipClient={othersMembershipClient}
                  onOthersMembershipClientChange={setOthersMembershipClient}
                  customerCreditRefreshKey={checkoutTopupRefreshKey}
                  creditFetchUserId={
                    othersMembershipClient?.user_id ??
                    bookingDataFromAPI?.user_id ??
                    bookingDetails?.user?.id ??
                    null
                  }
                  onMembershipClientTopUpClick={openCheckoutMembershipFlow}
                  isMembershipPayment={isMembershipSale}
                />
              );
            case 'saleinfo':
            case 'info':
            default:
              return (
                <>
                {/* {console.log(isServiceBooking, "isServiceBooking")} */}
                  {isServiceBooking ? (
                    <SaleInfoPage
                      showAddNotePopup={showAddNotePopup}
                      saleNote={saleNote}
                      showVoidSalePopup={onShowVoidSalePopup || showActionNotAvailableInSalesSidebar}
                      isSalesFlow={true}
                      onOpenRelatedSale={onOpenRelatedSale}
                    />
                  ) : (
                    <SaleBookingInfo
                      bookingId={bookingId}
                      bookingDetails={bookingDetails}
                      bookingDataFromAPI={bookingDataFromAPI}
                      customerDataFromAPI={customerDataFromAPI}
                      saleNote={saleNote}
                      showAddNotePopup={showAddNotePopup}
                      disableServiceListHover={true}
                      isSalesFlow={true}
                      onOpenRelatedSale={onOpenRelatedSale}
                      onAddPayment={() => {
                        // Navigate to payment step when "Add Payment" is clicked
                        setCurrentStep('payment');
                      }}
                    />
                  )}
                </>
              );
          }
        })()
      )}
      
      {/* Sale Note Popup */}
      {isSaleNotePopupActive && (
        <SaleNotePopup
          onClose={closeSaleNotePopup}
          editableNote={saleNote}
          addSaleNote={addSaleNoteHandler}
        />
      )}

      {isCheckoutTopupOpen && (
        <CustomerDetailsModal
          key={`topup-${checkoutTopupCustomer?.id ?? resolveTopupUserId() ?? 'sale'}`}
          customer={checkoutTopupCustomer ?? buildTopupCheckoutCustomer()}
          isOpen={isCheckoutTopupOpen}
          onClose={() => {
            restoreCheckoutPaymentMethodAfterTopup();
            checkoutTopupTargetCustomerRef.current = null;
            setCheckoutTopupCustomer(null);
            setIsOthersMemberTopupFlow(false);
            setIsCheckoutTopupOpen(false);
          }}
          autoOpenMembershipFlow={checkoutMembershipFlowType}
          membershipFlowOnly={true}
          skipSetMembershipPasswordAfterPayment={isOthersMemberTopupFlow}
          onMembershipPaymentSuccess={async (detail) => {
            setCheckoutTopupRefreshKey((previousValue) => previousValue + 1);
            const topupTargetClient = checkoutTopupTargetCustomerRef.current;
            const topupUserId = resolveTopupUserId();
            const paymentFullyCompleted = !(detail && detail.restoreCheckoutTotals === false);
            if (topupTargetClient?.user_id) {
              const patch = await fetchMembershipClientCreditPatch(topupUserId);
              if (patch) {
                setOthersMembershipClient((previous) => ({
                  ...(previous || topupTargetClient),
                  ...patch,
                  ...(paymentFullyCompleted ? { membership_status: "active" } : {}),
                }));
              }
            } else {
              await refreshCustomerCreditDetailsFromUser(
                topupUserId,
                setCustomerCreditDetails
              );
            }
            if (!paymentFullyCompleted) {
              return;
            }
            restoreCheckoutPaymentMethodAfterTopup();
            const grandTotal =
              Number(paymentSummary?.total) ||
              Number(bookingDataFromAPI?.grand_total) ||
              Number(bookingDataFromAPI?.sub_total) ||
              0;
            const remainingDue = computeRemainingCheckoutAmount(
              grandTotal,
              paymentHistory,
              checkoutAmountsBeforeTopupRef.current.amountDue
            );
            const remainingDueStr = remainingDue.toFixed(2);
            setAmountDue(remainingDueStr);
            const preservedCharge = parseFloat(
              checkoutAmountsBeforeTopupRef.current.amountToCharge
            );
            const chargeAmount =
              Number.isFinite(preservedCharge) && preservedCharge > 0
                ? Math.min(preservedCharge, remainingDue)
                : remainingDue;
            setAmountToCharge(chargeAmount.toFixed(2));
            const returnStep = checkoutStepBeforeTopupRef.current || 'confirmation';
            setCurrentStep(
              returnStep === 'payment' || returnStep === 'confirmation'
                ? returnStep
                : 'confirmation'
            );
          }}
        />
      )}
      {isMembershipPaymentDetailOpen && membershipDetail ? (
        <MembershipTxnPaymentDetailSheet
          detail={membershipDetail}
          selectedPayment={selectedMembershipPayment}
          allPayments={membershipPaymentDetails}
          saleId={membershipDetail?.sale_id}
          stackWidth="min(400px, 100vw)"
          onClose={() => {
            setIsMembershipPaymentDetailOpen(false);
            setSelectedMembershipPayment(null);
          }}
          onGoToSale={(saleId) => {
            onCloseSidebar?.();
            if (saleId && typeof onOpenRelatedSale === 'function') {
              window.setTimeout(() => {
                onOpenRelatedSale(saleId);
              }, 0);
            }
          }}
        />
      ) : null}
    </>
  );
};

export default BookingDetailsFromSalesSidebar;
